import axios from 'axios'
import { Loading, Message } from 'element-ui'
import router from '@/router'
import posthog from '../plugins/posthog'

// 新服务 API 基础地址映射

const API_MAP = {
  test: 'https://test2.fotoncard.com',        // test.fotoncard.com
  uat: 'https://epuser.fotoncard.com',        // uatdash.fotoncard.com
  prod: 'https://epuserlive.fotoncard.com'    // dash.fotoncard.com
}

/**
 * 根据当前域名判断环境
 */
function getEnvByHost(hostname) {
  if (hostname.includes('test.fotoncard.com')) {
    return 'test'
  }

  if (hostname.includes('uatdash.fotoncard.com')) {
    return 'uat'
  }

  if (hostname.includes('dashboard.fotoncard.com')) {
    return 'prod'
  }

  // 默认兜底
  return 'test'
}

/**
 * 获取当前 API Base URL
 */
function getBaseUrl() {
  const host = window.location.hostname
  const env = getEnvByHost(host)
  return API_MAP[env]
}

// 新服务默认接口前缀。
// Live: https://epuserlive.fotoncard.com
// UAT: https://epuser.fotoncard.com
// Test: https://test2.fotoncard.com
const DEFAULT_BASE_URL = getBaseUrl()+'/api/v1'
// 新服务签名使用的默认密钥。
const DEFAULT_SIGNATURE_SECRET = 'deckclient-web-sign-v1'
// 浏览器中保存 CSRF 值的 cookie 名称。
const CSRF_COOKIE_NAME = 'deckclient_csrf_token'
// 浏览器中保存登录态的 cookie 名称。
const AUTH_COOKIE_NAME = 'deckclient_auth_token'
// 新服务要求的 CSRF 请求头名称。
const CSRF_HEADER_NAME = 'X-CSRF-TOKEN'

// 从 document.cookie 中读取指定名称的 cookie 值。
function getCookie(name) {
  const item = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))

  return item ? decodeURIComponent(item.split('=').slice(1).join('=')) : ''
}

// 计算请求体的 SHA-256 十六进制摘要。
function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text)

  return crypto.subtle.digest('SHA-256', bytes).then((hash) => {
    return Array.from(new Uint8Array(hash))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  })
}

// 计算签名串的 HMAC-SHA256 十六进制结果。
function hmacSha256Hex(secret, text) {
  return crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  ).then((key) => {
    return crypto.subtle.sign('HMAC', key, new TextEncoder().encode(text))
  }).then((signature) => {
    return Array.from(new Uint8Array(signature))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  })
}

// 统一补全接口路径前缀斜杠，避免签名路径不一致。
function normalizeEndpoint(endpoint) {
  if (!endpoint) {
    return '/'
  }

  return endpoint.startsWith('/') ? endpoint : `/${endpoint}`
}

// 生成参与签名的 request uri，只保留 pathname 和 search。
function requestUri(baseURL, endpoint) {
  const normalizedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL
  const normalizedEndpoint = normalizeEndpoint(endpoint)
  const url = new URL(`${normalizedBaseURL}${normalizedEndpoint}`, window.location.origin)

  return `${url.pathname}${url.search}`
}

// 把请求体统一转换成参与签名的字符串。
function stringifyBody(data) {
  if (data === undefined || data === null || data === '') {
    return ''
  }

  if (typeof data === 'string') {
    return data
  }

  return JSON.stringify(data)
}

// 把新服务相关 cookie 拼成可复用的 Cookie 头字符串。
function buildCookieHeaderValue() {
  const authToken = getCookie(AUTH_COOKIE_NAME)
  const csrfToken = getCookie(CSRF_COOKIE_NAME)
  const items = []

  if (authToken) {
    items.push(`${AUTH_COOKIE_NAME}=${encodeURIComponent(authToken)}`)
  }

  if (csrfToken) {
    items.push(`${CSRF_COOKIE_NAME}=${encodeURIComponent(csrfToken)}`)
  }

  return items.join('; ')
}

// 创建独立的新服务请求客户端，避免影响旧 PHP 接口封装。
function createCardAuthClient(options = {}) {
  // 允许外部覆盖默认 baseURL。
  const baseURL = options.baseURL || DEFAULT_BASE_URL
  // 允许外部覆盖默认签名密钥。
  const signatureSecret = options.signatureSecret || DEFAULT_SIGNATURE_SECRET

  // 新服务专用 axios 实例，固定使用 JSON 和凭据模式。
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 60000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })

  // 请求 loading 实例，独立于旧服务。
  let loadingInstance = null

  // 请求拦截器：自动显示 loading。
  instance.interceptors.request.use((config) => {
    // 默认显示 loading，可通过 config.loading = false 关闭。
    if (config.loading !== false) {
      loadingInstance = Loading.service({ fullscreen: true })
    }
    return config
  }, (error) => {
    if (loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
    }
    return Promise.reject(error)
  })

  // 响应拦截器：自动关闭 loading，处理 401 token 过期。
  instance.interceptors.response.use((response) => {
    if (loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
    }

    // 兼容 HTTP 200 + code 401 的场景。
    const body = response.data
    if (body && body.code === 401) {
      posthog.reset()
      window.localStorage.clear()
      router.replace('/pages/login')
      Message.error(body.message || 'Login expired')
      return Promise.reject(new Error(body.message))
    }

    return response
  }, (error) => {
    if (loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
    }

    // 服务端返回了业务数据（如 500 + json body），正常返回给业务层处理。
    if (error.response && error.response.data) {
      // 401：token 过期，清除登录态并跳转登录页。
      const body = error.response.data
      if (body.code === 401) {
        posthog.reset()
        window.localStorage.clear()
        router.replace('/pages/login')
        Message.error(body.message || 'Login expired')
        return Promise.reject(new Error(body.message))
      }
      return error.response
    }
    return Promise.reject(error)
  })

  // 生成新服务要求的签名头集合。
  function signatureHeaders(method, endpoint, bodyText) {
    // 请求时间戳，服务端用它参与验签。
    const timestamp = Date.now().toString()
    // 请求体摘要，服务端会校验请求体是否被篡改。
    return sha256Hex(bodyText).then((bodyHash) => {
      // 按接口约定拼接待签名原文。
      const payload = [
        timestamp,
        method.toUpperCase(),
        requestUri(baseURL, endpoint),
        bodyHash
      ].join('\n')

      // 用共享密钥计算签名值。
      return hmacSha256Hex(signatureSecret, payload).then((signature) => {
        return {
          'X-Client-Timestamp': timestamp,
          'X-Client-Body-SHA256': bodyHash,
          'X-Client-Sign': signature
        }
      })
    })
  }

  // 根据接口类型组装最终请求头。
  function buildHeaders(method, endpoint, data, options = {}) {
    // 先把原始请求体转换成签名需要的字符串格式。
    const bodyText = stringifyBody(data)

    return signatureHeaders(method, endpoint, bodyText).then((signatureHeaderMap) => {
      // 合并调用方自定义头和签名头。
      const headers = {
        ...(options.headers || {}),
        ...signatureHeaderMap
      }

      // 默认自动附加 CSRF 头，除非调用方显式关闭。
      if (options.includeCsrf !== false) {
        const csrfToken = getCookie(CSRF_COOKIE_NAME)
        if (csrfToken) {
          headers[CSRF_HEADER_NAME] = csrfToken
        }
      }

      // 按需拼接 Cookie 头字符串，供旧接口桥接时复用。
      if (options.includeCookieHeader) {
        const cookieHeaderValue = buildCookieHeaderValue()
        if (cookieHeaderValue) {
          headers.Cookie = cookieHeaderValue
        }
      }

      return headers
    })
  }

  // 统一发送新服务请求，并在发送前补齐签名相关头。
  function request(config) {
    // 默认按 GET 处理未显式声明的方法。
    const method = (config.method || 'GET').toUpperCase()
    // 默认根路径，避免空 url 影响签名。
    let endpoint = config.url || '/'

    // GET 请求需将 query 参数拼入 endpoint，确保签名 URI 与请求 URI 一致。
    if (method === 'GET' && config.params) {
      const search = new URLSearchParams(config.params).toString()
      if (search) {
        endpoint += endpoint.includes('?') ? '&' + search : '?' + search
      }
      // 参数已拼入 URL，避免 axios 再次拼接导致重复。
      delete config.params
    }

    // GET 请求不参与 body 签名，统一按空字符串处理。
    const bodyData = method === 'GET' ? '' : config.data

    // 生成本次请求要带的最终头信息。
    return buildHeaders(method, endpoint, bodyData, {
      headers: config.headers,
      includeCsrf: config.includeCsrf,
      includeCookieHeader: config.includeCookieHeader
    }).then((headers) => {
      return instance({
        ...config,
        method,
        url: endpoint,
        headers
      })
    })
  }

  return {
    // 暴露底层实例，便于特殊场景直接使用。
    instance,
    // 暴露通用请求方法，便于外层 service 统一调用。
    request,
    // 暴露通用 cookie 读取方法，便于外层按名称取值。
    getCookie,
    // 获取当前浏览器中的新服务登录 token cookie。
    getAuthToken() {
      return getCookie(AUTH_COOKIE_NAME)
    },
    // 获取当前浏览器中的新服务 CSRF cookie。
    getCsrfToken() {
      return getCookie(CSRF_COOKIE_NAME)
    },
    // 暴露 Cookie 头拼接方法，给旧系统桥接请求复用。
    buildCookieHeaderValue,
    // 暴露头信息构建方法，给特殊请求场景复用。
    buildHeaders,
    // GET 简写方法。
    get(url, config = {}) {
      return request({
        ...config,
        method: 'GET',
        url
      })
    },
    // POST 简写方法。
    post(url, data = {}, config = {}) {
      return request({
        ...config,
        method: 'POST',
        url,
        data
      })
    }
  }
}

// 导出默认实例，供业务代码直接使用。
const cardAuthClient = createCardAuthClient()

export {
  AUTH_COOKIE_NAME,
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
  DEFAULT_BASE_URL,
  DEFAULT_SIGNATURE_SECRET,
  buildCookieHeaderValue,
  createCardAuthClient,
  getCookie,
  hmacSha256Hex,
  requestUri,
  sha256Hex,
  stringifyBody
}

export default cardAuthClient
